import { API } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { AccountInterface, AccountKind, FEATURE_FLAG_REGISTRY, FeatureFlagKey, FeatureFlagMap, OrgInterface } from "@swivl-mono/types";
import { Button, ButtonVariant, Modal, Select, SelectItem } from "@swivl-mono/ui";
import { useEffect, useMemo, useState } from "react";

import { Member } from "../../../Models/Member/Member.Current";
import { NavBar } from "../../../Models/NavBar/NavBar.Model";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";

type Tab = "orgs" | "accounts";

const OFF_VALUE = "__off__";

const THRESHOLD_OPTIONS: SelectItem[] = [
  { value: OFF_VALUE, label: "Off" },
  { value: AccountKind.Customer, label: "Customer (everyone)" },
  { value: AccountKind.SwivlStaff, label: "Swivl Staff" },
  { value: AccountKind.SwivlAdmin, label: "Swivl Admin" }
];

// Static base list. The SwivlAdmin option is filtered out at render time
// for non-admin viewers — see `kindOptionsForViewer` below.
const ALL_KIND_OPTIONS: SelectItem[] = [
  { value: AccountKind.Customer, label: "Customer" },
  { value: AccountKind.SwivlStaff, label: "Swivl Staff" },
  { value: AccountKind.SwivlAdmin, label: "Swivl Admin" }
];

// Only SwivlAdmin can promote/demote at the top tier. Non-admins still see
// the kind picker, but the SwivlAdmin option is removed so they can't
// accidentally (or intentionally) escalate someone past their own rank.
// Server-side enforcement mirrors this — see Accounts.Service.updateKind.
function kindOptionsForViewer(viewerIsAdmin: boolean): SelectItem[] {
  if (viewerIsAdmin) return ALL_KIND_OPTIONS;
  return ALL_KIND_OPTIONS.filter((o) => o.value !== AccountKind.SwivlAdmin);
}

const REGISTRY_ENTRIES = Object.values(FEATURE_FLAG_REGISTRY);

export function FeatureFlagsAdminView() {
  const [tab, setTab] = useState<Tab>("orgs");

  useEffect(() => {
    NavBar.set(<NavBar.Component.Title title="Feature Flags" />, null, null, "Feature Flags");
  }, []);

  return (
    <div className="flex h-full flex-col">
      <div className="flex border-b border-grey-200 bg-grey-50">
        <TabButton label="Org Flags" active={tab === "orgs"} onClick={() => setTab("orgs")} />
        <TabButton label="Accounts" active={tab === "accounts"} onClick={() => setTab("accounts")} />
      </div>
      <div className="flex-1 overflow-auto p-6">{tab === "orgs" ? <OrgFlagsPanel /> : <AccountsPanel />}</div>
    </div>
  );
}

interface TabButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
}
function TabButton({ label, active, onClick }: TabButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={filterClasses(
        "border-r border-grey-200 px-4 py-2 text-sm font-medium tracking-wide uppercase transition-colors",
        active ? "bg-white text-primary-600" : "text-grey-700 hover:bg-grey-100"
      )}
    >
      {label}
    </button>
  );
}

/** The "Set for all orgs" dialog's state: which flag, and the value picked so far. */
interface BulkFlagDraft {
  key: FeatureFlagKey;
  value: string;
}

function OrgFlagsPanel(): JSX.Element {
  const [orgs, setOrgs] = useState<OrgInterface[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [bulk, setBulk] = useState<BulkFlagDraft | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);

  const loadOrgs = async (): Promise<void> => {
    const res = await API.Org.getAllOrgs();
    setOrgs((res || []).sort((a, b) => a.name.localeCompare(b.name)));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadOrgs()
      .catch(() => !cancelled && AlertsModel.errorToast("Could not load orgs"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter((o) => o.name.toLowerCase().includes(q) || String(o.id).includes(q));
  }, [orgs, filter]);

  const handleChange = async (org: OrgInterface, key: FeatureFlagKey, value: string) => {
    const next: FeatureFlagMap = { ...(org.featureFlags || {}) };
    if (value === OFF_VALUE) {
      delete next[key];
    } else {
      next[key] = value as AccountKind;
    }
    // optimistic
    setOrgs((prev) => prev.map((o) => (o.id === org.id ? { ...o, featureFlags: next } : o)));
    try {
      await API.Org.updateFeatureFlags(org.id, next);
    } catch {
      AlertsModel.errorToast(`Failed to update ${org.name}`);
      // revert
      setOrgs((prev) => prev.map((o) => (o.id === org.id ? { ...o, featureFlags: org.featureFlags } : o)));
    }
  };

  // One flag, every org (not just the filtered rows), one server statement. Built for the
  // Outbound Gate launch, where five streams have to go on for every production org at once.
  const handleBulkApply = async (): Promise<void> => {
    if (!bulk) return;
    setBulkSaving(true);
    try {
      const { updated } = await API.Org.setFeatureFlagForAllOrgs(bulk.key, bulk.value === OFF_VALUE ? null : (bulk.value as AccountKind));
      await loadOrgs();
      AlertsModel.successToast(`${FEATURE_FLAG_REGISTRY[bulk.key].label} set on ${updated} org${updated === 1 ? "" : "s"}`);
      setBulk(null);
    } catch {
      AlertsModel.errorToast(`Failed to set ${FEATURE_FLAG_REGISTRY[bulk.key].label} for all orgs`);
    } finally {
      setBulkSaving(false);
    }
  };
  const bulkValueLabel = bulk
    ? (THRESHOLD_OPTIONS.find((o): o is Extract<SelectItem, { value: string; label: string }> => "value" in o && o.value === bulk.value)?.label ??
      bulk.value)
    : "";

  return (
    <div>
      <p className="mb-3 text-sm text-grey-600">
        Set the minimum <span className="font-semibold">AccountKind</span> required to see each feature on each org. &ldquo;Off&rdquo; hides the
        feature from everyone on that org. See <span className="font-mono text-xs">docs/snowy/guides/feature-flags.md</span>.
      </p>
      <input
        type="text"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter by org name or id…"
        className="mb-3 w-full max-w-sm rounded border border-grey-300 px-3 py-1.5 text-sm"
      />
      {loading ? (
        <p className="text-sm text-grey-500">Loading orgs…</p>
      ) : (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-grey-200 text-left text-xs tracking-wide text-grey-600 uppercase">
              <th className="py-2 pr-4">Org</th>
              {REGISTRY_ENTRIES.map((def) => (
                <th key={def.key} className="px-2 py-2" title={def.description}>
                  <div>{def.label}</div>
                  <button
                    type="button"
                    onClick={() => setBulk({ key: def.key, value: AccountKind.Customer })}
                    className="mt-0.5 cursor-pointer text-xs font-normal tracking-normal text-orange-600 normal-case hover:underline"
                  >
                    Set for all orgs…
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((org) => (
              <tr key={org.id} className="border-b border-grey-100">
                <td className="py-2 pr-4">
                  <div className="font-medium">{org.name}</div>
                  <div className="text-xs text-grey-500">id {org.id}</div>
                </td>
                {REGISTRY_ENTRIES.map((def) => {
                  const current = org.featureFlags?.[def.key] ?? OFF_VALUE;
                  return (
                    <td key={def.key} className="px-2 py-2">
                      <Select
                        options={THRESHOLD_OPTIONS}
                        selectedValues={[current]}
                        hideClear
                        onChange={(vals) => handleChange(org, def.key, vals[0])}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Modal isOpen={bulk !== null} onClose={(open) => !open && !bulkSaving && setBulk(null)} title="Set for all orgs" hasTitleBorder>
        {bulk && (
          <div className="flex flex-col gap-4 p-4">
            <p className="text-sm text-grey-700">
              <span className="font-semibold">{FEATURE_FLAG_REGISTRY[bulk.key].label}</span> on every org — all{" "}
              <span className="font-semibold">{orgs.length}</span> of them, not only the rows matching the filter. Other flags on each org are left as
              they are.
            </p>
            <p className="text-xs text-grey-500">{FEATURE_FLAG_REGISTRY[bulk.key].description}</p>
            {/* Radios, not the Select: a popover picker nested in this dialog dismissed the dialog on the first click (2026-09-22 test). */}
            <div className="flex flex-col gap-2 text-sm text-grey-900">
              {THRESHOLD_OPTIONS.map((option) =>
                "value" in option ? (
                  <label key={option.value} className="flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      name="bulk-flag-value"
                      checked={bulk.value === option.value}
                      onChange={() => setBulk({ ...bulk, value: option.value })}
                      className="accent-orange-500"
                    />
                    {option.label}
                  </label>
                ) : null
              )}
            </div>
            <p className="text-sm text-grey-700">
              This will set <span className="font-semibold">{FEATURE_FLAG_REGISTRY[bulk.key].label}</span> to{" "}
              <span className="font-semibold">{bulkValueLabel}</span> on {orgs.length} org{orgs.length === 1 ? "" : "s"}.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant={ButtonVariant.GHOST} onClick={() => setBulk(null)} disabled={bulkSaving}>
                Cancel
              </Button>
              <Button variant={ButtonVariant.PRIMARY} onClick={handleBulkApply} disabled={bulkSaving}>
                {bulkSaving ? "Applying…" : `Apply to ${orgs.length} orgs`}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function AccountsPanel(): JSX.Element {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<AccountInterface[]>([]);
  const [searching, setSearching] = useState(false);
  const viewerIsAdmin = Member.isSwivlAdmin;
  const kindOptions = useMemo(() => kindOptionsForViewer(viewerIsAdmin), [viewerIsAdmin]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = window.setTimeout(() => {
      API.Account.searchAccounts(trimmed)
        .then((res) => {
          if (cancelled) return;
          setResults(res || []);
        })
        .catch(() => AlertsModel.errorToast("Search failed"))
        .finally(() => !cancelled && setSearching(false));
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [query]);

  const handleKindChange = async (account: AccountInterface, kind: AccountKind) => {
    const prev = account.kind;
    setResults((rs) => rs.map((a) => (a.id === account.id ? { ...a, kind } : a)));
    try {
      await API.Account.updateKind(account.id, kind);
    } catch {
      AlertsModel.errorToast(`Failed to update ${account.email}`);
      setResults((rs) => rs.map((a) => (a.id === account.id ? { ...a, kind: prev } : a)));
    }
  };

  return (
    <div>
      <p className="mb-3 text-sm text-grey-600">
        Search by email, first/last name, or account id. Promote accounts to <span className="font-mono text-xs">Swivl Staff</span> for general
        internal-preview access, or <span className="font-mono text-xs">Swivl Admin</span> for top-tier operations.
        {!viewerIsAdmin && <> Only Swivl Admins can promote others to Swivl Admin.</>}
      </p>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search accounts… (min 2 chars)"
        className="mb-3 w-full max-w-sm rounded border border-grey-300 px-3 py-1.5 text-sm"
      />
      {searching ? (
        <p className="text-sm text-grey-500">Searching…</p>
      ) : results.length === 0 && query.trim().length >= 2 ? (
        <p className="text-sm text-grey-500">No accounts match.</p>
      ) : (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-grey-200 text-left text-xs tracking-wide text-grey-600 uppercase">
              <th className="py-2 pr-4">Account</th>
              <th className="py-2 pr-4">Email</th>
              <th className="py-2">Kind</th>
            </tr>
          </thead>
          <tbody>
            {results.map((account) => (
              <tr key={account.id} className="border-b border-grey-100">
                <td className="py-2 pr-4">
                  <div className="font-medium">
                    {account.firstName} {account.lastName}
                  </div>
                  <div className="text-xs text-grey-500">id {account.id}</div>
                </td>
                <td className="py-2 pr-4">{account.email}</td>
                <td className="py-2">
                  <Select
                    options={kindOptions}
                    selectedValues={[account.kind ?? AccountKind.Customer]}
                    hideClear
                    onChange={(vals) => handleKindChange(account, vals[0] as AccountKind)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
